import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    X, User, Mail, BedDouble, CheckCircle, Calendar, Wallet, Smartphone,
    Building2, AlertTriangle, Clock
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { bookingsAPI, roomsAPI, categoriesAPI, settingsAPI } from '../../services/api';
import LoadingSpinner from '../ui/LoadingSpinner';
import DateTimePicker from '../ui/DateTimePicker';
import FormField, { fieldClass } from '../booking/FormField';
import PnrInput from '../booking/PnrInput';
import PhoneInput from '../booking/PhoneInput';
import IdProofInput from '../booking/IdProofInput';
import DurationSelector from '../booking/DurationSelector';
import CategorySelect from '../booking/CategorySelect';
import PriceSummary from '../booking/PriceSummary';
import {
    DEFAULT_COUNTRY_CODE,
    GUEST_NAME_MAX,
    EMAIL_MAX,
    validateBookingForm
} from '../../utils/bookingValidation';

const EMPTY_FORM = {
    guestName: '',
    pnr: '',
    countryCode: DEFAULT_COUNTRY_CODE,
    mobile: '',
    email: '',
    categoryId: '',
    roomId: '',
    durationSlot: '12hr',
    immediateCheckIn: true,
    idCardImage: null,
    idCardNumber: '',
    idInputType: 'image',
    paymentMode: 'counter'
};

/**
 * Create a booking at the counter.
 *
 * `station` is the station being booked for — for a station admin that is their own
 * station; for a super admin it is whatever they picked in the dropdown next to the button.
 * Categories and rooms are both fetched scoped to it, so the form can only ever offer room
 * types that actually exist there.
 */
const AdminBookingModal = ({ isOpen, onClose, station, stationId, onSuccess }) => {
    // Accept either a full station object or a bare id, so existing callers keep working.
    const resolvedStationId = station?._id || stationId || '';
    const stationName = station?.name || null;
    const stationCode = station?.code || null;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [categories, setCategories] = useState([]);
    const [gstInfo, setGstInfo] = useState({ percentage: 5, enabled: true });
    const [loadError, setLoadError] = useState(null);

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [scheduledDate, setScheduledDate] = useState(null);
    const [scheduledTime, setScheduledTime] = useState(null);
    const [availabilityData, setAvailabilityData] = useState(null);
    const [loadingAvailability, setLoadingAvailability] = useState(false);

    const patch = useCallback((changes) => {
        setFormData((prev) => ({ ...prev, ...changes }));
        // Clear the inline error for anything the user just touched.
        setErrors((prev) => {
            const next = { ...prev };
            Object.keys(changes).forEach((key) => delete next[key]);
            if ('idCardImage' in changes || 'idCardNumber' in changes || 'idInputType' in changes) {
                delete next.idProof;
            }
            if ('mobile' in changes || 'countryCode' in changes) delete next.mobile;
            return next;
        });
    }, []);

    const fetchRoomsAndCategories = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            // Both calls are scoped to this station. Fetching every station's categories is
            // what let an admin choose a room type their station does not have.
            const [roomsRes, categoriesRes, gstRes] = await Promise.all([
                roomsAPI.getAll({ station: resolvedStationId, status: 'available' }),
                categoriesAPI.getAll({ station: resolvedStationId }),
                settingsAPI.getGST()
            ]);
            setRooms(roomsRes.data.rooms || []);
            setCategories(categoriesRes.data.categories || []);
            if (gstRes.data?.gst) {
                setGstInfo({ percentage: gstRes.data.gst.percentage, enabled: gstRes.data.gst.enabled });
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoadError('Could not load room categories for this station. Close and try again.');
            toast.error('Failed to load rooms and categories');
        } finally {
            setLoading(false);
        }
    }, [resolvedStationId]);

    useEffect(() => {
        if (isOpen && resolvedStationId) fetchRoomsAndCategories();
    }, [isOpen, resolvedStationId, fetchRoomsAndCategories]);

    // Switching station (super admin) invalidates any category/room already chosen.
    useEffect(() => {
        setFormData((prev) => ({ ...prev, categoryId: '', roomId: '' }));
        setErrors({});
    }, [resolvedStationId]);

    useEffect(() => {
        if (!scheduledDate || formData.immediateCheckIn || !resolvedStationId) {
            setAvailabilityData(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoadingAvailability(true);
            try {
                const dateStr = format(scheduledDate, 'yyyy-MM-dd');
                const res = await bookingsAPI.getAvailability(resolvedStationId, dateStr, formData.categoryId || undefined);
                if (!cancelled) setAvailabilityData(res.data);
            } catch {
                if (!cancelled) setAvailabilityData(null);
            } finally {
                if (!cancelled) setLoadingAvailability(false);
            }
        })();
        return () => { cancelled = true; };
    }, [scheduledDate, formData.immediateCheckIn, resolvedStationId, formData.categoryId]);

    // Rooms belong to a category; changing the category must drop a room chosen under the
    // old one, otherwise a stale roomId is submitted and rejected by the server.
    const handleCategoryChange = (categoryId) => patch({ categoryId, roomId: '' });

    const handleCheckInModeChange = (immediate) => {
        patch({ immediateCheckIn: immediate, roomId: immediate ? formData.roomId : '' });
        if (immediate) {
            setScheduledDate(null);
            setScheduledTime(null);
        }
    };

    const filteredRooms = useMemo(() => {
        if (!formData.categoryId) return [];
        return rooms.filter((room) => (room.category?._id || room.category) === formData.categoryId);
    }, [rooms, formData.categoryId]);

    const selectedCategory = categories.find((c) => c._id === formData.categoryId);
    const selectedPrice = selectedCategory
        ? (selectedCategory.pricing?.[formData.durationSlot] ?? selectedCategory.basePrice)
        : null;

    const handleClose = () => {
        setFormData(EMPTY_FORM);
        setErrors({});
        setScheduledDate(null);
        setScheduledTime(null);
        setAvailabilityData(null);
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateBookingForm(
            { ...formData, scheduledDate, scheduledTime },
            {
                requireCategory: true,
                requireRoom: formData.immediateCheckIn,
                requireSchedule: !formData.immediateCheckIn
            }
        );

        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            toast.error(Object.values(validationErrors)[0]);
            return;
        }
        setErrors({});

        setSubmitting(true);
        try {
            const data = new FormData();
            data.append('guestName', formData.guestName.trim());
            // Both spellings are sent: the API accepts either, and older deployments of the
            // backend only understand `prn`.
            data.append('pnr', formData.pnr);
            data.append('prn', formData.pnr);
            data.append('countryCode', formData.countryCode);
            data.append('mobile', formData.mobile);
            if (formData.email.trim()) data.append('email', formData.email.trim());
            data.append('categoryId', formData.categoryId);
            if (formData.immediateCheckIn && formData.roomId) data.append('roomId', formData.roomId);
            data.append('durationSlot', formData.durationSlot);
            data.append('immediateCheckIn', String(formData.immediateCheckIn));
            data.append('station', resolvedStationId);
            data.append('paymentMode', formData.paymentMode);

            if (formData.idInputType === 'image' && formData.idCardImage) {
                data.append('idCardImage', formData.idCardImage);
            }
            if (formData.idInputType === 'text' && formData.idCardNumber.trim()) {
                data.append('idCardNumber', formData.idCardNumber.trim());
            }
            if (!formData.immediateCheckIn && scheduledDate && scheduledTime) {
                data.append('scheduledDate', format(scheduledDate, 'yyyy-MM-dd'));
                data.append('scheduledTimeSlot', scheduledTime);
            }

            const response = await bookingsAPI.adminCreate(data);

            if (response.data.paymentRedirectUrl) {
                toast.success('Booking created! Opening payment page…');
                window.open(response.data.paymentRedirectUrl, '_blank');
            } else if (response.data.paymentFallback) {
                toast.success(response.data.message, { duration: 5000 });
            } else {
                toast.success(response.data.message);
            }
            onSuccess?.();
            handleClose();
        } catch (error) {
            console.error('Error creating booking:', error);
            const { error: message, field } = error.response?.data || {};
            // The API now names the field it rejected, so the error lands on that input
            // instead of only appearing as a toast.
            if (field) setErrors((prev) => ({ ...prev, [field]: message }));
            toast.error(message || 'Failed to create booking');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const toggleClass = (active, activeColor) =>
        `flex-1 py-2.5 px-3 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-1.5 ${
            active ? `${activeColor} text-white shadow` : 'text-midnight-600 hover:bg-midnight-100'
        }`;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-midnight-900/60 backdrop-blur-sm" onClick={handleClose} />

                <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-xl animate-slide-up my-8">
                    {/* Header — always states which station is being booked, so a super admin
                        switching between stations can never be in doubt. */}
                    <div className="flex items-start justify-between gap-3 p-5 border-b border-midnight-100">
                        <div className="text-left min-w-0">
                            <h3 className="text-lg font-display font-semibold text-midnight-900">Create Booking</h3>
                            {stationName ? (
                                <div className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-railway-50 border border-railway-200">
                                    <Building2 className="w-3.5 h-3.5 text-railway-600 flex-shrink-0" />
                                    <span className="text-sm font-medium text-railway-800 truncate">
                                        {stationName}{stationCode ? ` (${stationCode})` : ''}
                                    </span>
                                </div>
                            ) : (
                                <p className="text-sm text-midnight-500">Book a room directly for a passenger</p>
                            )}
                        </div>
                        <button
                            onClick={handleClose}
                            aria-label="Close"
                            className="p-2 text-midnight-400 hover:text-midnight-600 hover:bg-midnight-50 rounded-lg transition-colors flex-shrink-0"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <LoadingSpinner size="lg" />
                        </div>
                    ) : loadError ? (
                        <div className="p-8 text-center">
                            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                            <p className="text-midnight-700">{loadError}</p>
                            <button onClick={fetchRoomsAndCategories} className="btn-secondary mt-4">Retry</button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="p-5 space-y-4 text-left" noValidate>
                            <FormField label="Guest Name" htmlFor="guestName" required error={errors.guestName}>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-midnight-400 pointer-events-none" />
                                    <input
                                        id="guestName"
                                        type="text"
                                        value={formData.guestName}
                                        onChange={(e) => patch({ guestName: e.target.value.slice(0, GUEST_NAME_MAX) })}
                                        maxLength={GUEST_NAME_MAX}
                                        placeholder="Full name as on the ID"
                                        className={fieldClass(errors.guestName, 'pl-10 py-2.5')}
                                    />
                                </div>
                            </FormField>

                            <PnrInput
                                value={formData.pnr}
                                onChange={(pnr) => patch({ pnr })}
                                error={errors.pnr}
                            />

                            <PhoneInput
                                countryCode={formData.countryCode}
                                mobile={formData.mobile}
                                onChange={patch}
                                error={errors.mobile}
                            />

                            <FormField
                                label="Email"
                                htmlFor="email"
                                error={errors.email}
                                hint="Optional — the booking confirmation is emailed here"
                            >
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-midnight-400 pointer-events-none" />
                                    <input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => patch({ email: e.target.value.slice(0, EMAIL_MAX) })}
                                        maxLength={EMAIL_MAX}
                                        placeholder="guest@example.com"
                                        className={fieldClass(errors.email, 'pl-10 py-2.5')}
                                    />
                                </div>
                            </FormField>

                            <IdProofInput
                                inputType={formData.idInputType}
                                onInputTypeChange={(idInputType) => patch({ idInputType })}
                                imageFile={formData.idCardImage}
                                onImageChange={(idCardImage) => patch({ idCardImage })}
                                idCardNumber={formData.idCardNumber}
                                onIdCardNumberChange={(idCardNumber) => patch({ idCardNumber })}
                                error={errors.idProof}
                            />

                            <DurationSelector
                                value={formData.durationSlot}
                                onChange={(durationSlot) => patch({ durationSlot })}
                            />

                            <CategorySelect
                                categories={categories}
                                value={formData.categoryId}
                                onChange={handleCategoryChange}
                                durationSlot={formData.durationSlot}
                                error={errors.categoryId}
                                stationLabel={stationName ? `${stationName}${stationCode ? ` (${stationCode})` : ''}` : 'this station'}
                            />

                            {/* Check-in timing. Moved above the room picker: whether a room is
                                needed at all depends on this choice. */}
                            <FormField label="Check-in" required>
                                <div className="flex gap-2 bg-midnight-50 rounded-xl p-1.5">
                                    <button
                                        type="button"
                                        onClick={() => handleCheckInModeChange(true)}
                                        className={toggleClass(formData.immediateCheckIn, 'bg-green-500')}
                                    >
                                        <Clock className="w-4 h-4" />
                                        Immediate Check-in
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleCheckInModeChange(false)}
                                        className={toggleClass(!formData.immediateCheckIn, 'bg-blue-500')}
                                    >
                                        <Calendar className="w-4 h-4" />
                                        Schedule For Later
                                    </button>
                                </div>
                            </FormField>

                            {formData.immediateCheckIn ? (
                                <FormField
                                    label={<><BedDouble className="w-4 h-4 inline mr-1" />Select Room</>}
                                    htmlFor="roomId"
                                    required
                                    error={errors.roomId}
                                >
                                    <select
                                        id="roomId"
                                        value={formData.roomId}
                                        onChange={(e) => patch({ roomId: e.target.value })}
                                        className={fieldClass(errors.roomId, 'py-2.5')}
                                        disabled={!formData.categoryId}
                                    >
                                        <option value="">
                                            {formData.categoryId ? 'Select an available room' : 'Select a category first'}
                                        </option>
                                        {filteredRooms.map((room) => (
                                            <option key={room._id} value={room._id}>
                                                Room {room.roomNumber} · Floor {room.floor || 'G'}
                                            </option>
                                        ))}
                                    </select>
                                    {formData.categoryId && filteredRooms.length === 0 && (
                                        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-600">
                                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                                            <span>
                                                Every room in this category is occupied or under maintenance.
                                                Pick another category, or use "Schedule For Later".
                                            </span>
                                        </p>
                                    )}
                                </FormField>
                            ) : (
                                <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 text-sm text-midnight-600 flex items-start gap-2">
                                    <BedDouble className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
                                    <span>
                                        A specific room is assigned automatically at check-in. An advance booking
                                        only reserves this category for the chosen date &amp; time, keeping the
                                        rooms free for other dates.
                                    </span>
                                </div>
                            )}

                            {!formData.immediateCheckIn && (
                                <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                                    <label className="block text-sm font-medium text-midnight-700 mb-2">
                                        <Calendar className="w-4 h-4 inline mr-1" />
                                        Check-in Date &amp; Time<span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    <DateTimePicker
                                        selectedDate={scheduledDate}
                                        selectedTime={scheduledTime}
                                        onSelect={(date, time) => { setScheduledDate(date); setScheduledTime(time); }}
                                        onDateChange={(date) => { setScheduledDate(date); setScheduledTime(null); }}
                                        availabilityData={availabilityData}
                                        loading={loadingAvailability}
                                        maxDaysAhead={30}
                                    />
                                    {(errors.scheduledDate || errors.scheduledTime) && (
                                        <p className="mt-2 text-xs text-red-600">
                                            {errors.scheduledDate || errors.scheduledTime}
                                        </p>
                                    )}
                                </div>
                            )}

                            <FormField
                                label="Payment Mode"
                                required
                                hint={formData.paymentMode === 'online'
                                    ? 'A PhonePe payment link is generated for the passenger'
                                    : 'Cash or card collected at the counter'}
                            >
                                <div className="flex gap-2 bg-midnight-50 rounded-xl p-1.5">
                                    <button
                                        type="button"
                                        onClick={() => patch({ paymentMode: 'counter' })}
                                        className={toggleClass(formData.paymentMode === 'counter', 'bg-green-500')}
                                    >
                                        <Wallet className="w-4 h-4" />
                                        Pay at Counter
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => patch({ paymentMode: 'online' })}
                                        className={toggleClass(formData.paymentMode === 'online', 'bg-indigo-500')}
                                    >
                                        <Smartphone className="w-4 h-4" />
                                        Online Payment
                                    </button>
                                </div>
                            </FormField>

                            <PriceSummary
                                subtotal={selectedPrice}
                                gstPercentage={gstInfo.percentage}
                                gstEnabled={gstInfo.enabled}
                                label={selectedCategory ? `${selectedCategory.name} · ${formData.durationSlot}` : undefined}
                            />

                            <div className="flex gap-3 pt-1">
                                <button type="button" onClick={handleClose} className="btn-secondary flex-1 py-2.5">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className={`flex-1 flex items-center justify-center gap-2 font-medium py-2.5 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                        formData.paymentMode === 'online'
                                            ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                            : 'btn-primary'
                                    }`}
                                >
                                    {submitting ? (
                                        <><LoadingSpinner size="sm" /><span>Creating…</span></>
                                    ) : formData.paymentMode === 'online' ? (
                                        <><Smartphone className="w-4 h-4" /><span>Create &amp; Pay Online</span></>
                                    ) : (
                                        <><CheckCircle className="w-4 h-4" /><span>Create Booking</span></>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminBookingModal;
